/** URL segment of Learning under the account (`/accounts/:accountId/learning`). */
export const LEARNING_ROUTE_SEGMENT = 'learning';

/** Child segments. A stage lives at `learning/<stageId>`; these two are fixed pages. */
export const NEWS_SEGMENT = 'novedades';
export const MCP_SEGMENT = 'mcp';

/**
 * The releases, as build-docs.js publishes them next to the docs hub (CIB-1263). Same origin
 * as the app, so the docs edge function lets the request through on the app's own referer.
 */
export const RELEASES_URL = '/docs/guides/novedades.json';

/**
 * The docs hub pages. App routes, not files: AppRouter renders each one in an iframe behind
 * `ProtectedRoute requiredRoles={DOCS_ROLES}`, so only those roles see the links.
 */
export const DOCS_LINKS = {
  /** The platform API generated from the SAM templates (CIB-1305). */
  swagger: '/docs/api',
  storybook: '/docs/storybook',
  backend: '/docs/backend',
  guides: '/docs',
} as const;

/** One operation in the API reference: Swagger UI's deep link, `#/<tag>/<operationId>`. */
export const apiReferenceLink = (tag: string, operationId: string): string =>
  `${DOCS_LINKS.swagger}#/${encodeURIComponent(tag)}/${encodeURIComponent(operationId)}`;

/** The roles AppRouter lets into /docs. Kept in step with its `requiredRoles`. */
export const DOCS_ROLES: readonly string[] = ['admin', 'superadmin', 'implementador'];

/**
 * Only reads run from the try-it panel. A write from a learning page could change a customer's
 * agent by accident; for those the panel shows the request and sends the user to the screen.
 */
export const TRY_METHODS: ReadonlySet<string> = new Set(['GET']);

/** The longest response body the try-it panel prints before cutting it. */
export const MAX_RESPONSE_CHARS = 20_000;

/** How long after its release a task keeps its "New" or "Changed" label. */
export const BADGE_DAYS = 14;

/** Where the user's progress is kept, per user and account (this browser only). */
export const PROGRESS_STORAGE_PREFIX = 'silia-learning:progress';

/** Where an admin's choice to see the technical layer is kept, per user (this browser). */
export const TECHNICAL_STORAGE_PREFIX = 'silia-learning:technical';

/** Where each reader's practice agent and table are kept, per user and account (this browser). */
export const PRACTICE_STORAGE_PREFIX = 'silia-learning:practice';

/**
 * The account set up for practising (CIB-1328), per environment. Outside it, what the reader opens
 * and saves changes a customer's real data, so Learning says so. Empty until it is provisioned.
 */
export const practiceAccountId = (): string =>
  String(process.env.REACT_APP_LEARNING_PRACTICE_ACCOUNT ?? '').trim();

/** Where a practice agent or table is created: the app's own screens, opened in a new tab. */
export const CREATE_PRACTICE_PATHS = {
  agent: '/accounts/:accountId/agents/new/start',
  table: '/accounts/:accountId/data-views/new',
} as const;
